import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import type {
  IndianMarketCharges,
  PlanExecutionData,
  PlanPurchase,
  PlanSell,
} from '../../models/PlanExecutionTypes';
import type { PlanCapitalAllocation } from '../../models/StockPickTypes';

type ApiEnvelope<T> = { data: T; message?: string; statusCode: number };

const BASE = `${LEAD_API_PREFIX}/capitals`;

function formatApiDate(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (Array.isArray(value) && value.length >= 3) {
    const y = value[0];
    const m = String(value[1]).padStart(2, '0');
    const d = String(value[2]).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return '';
}

function mapCharges(c?: Partial<IndianMarketCharges> | null): IndianMarketCharges {
  return {
    stt: c?.stt ?? 0,
    brokerage: c?.brokerage ?? 0,
    gstOnBrokerage: c?.gstOnBrokerage ?? 0,
    exchangeTxnCharges: c?.exchangeTxnCharges ?? 0,
    sebiTurnoverFees: c?.sebiTurnoverFees ?? 0,
    stampDuty: c?.stampDuty ?? 0,
  };
}

function mapPurchase(raw: Record<string, unknown>): PlanPurchase {
  const id = raw.id != null && String(raw.id).trim() !== '' ? String(raw.id) : undefined;
  return {
    id,
    tradeDate: formatApiDate(raw.tradeDate),
    quantity: Number(raw.quantity ?? 0),
    pricePerShare: Number(raw.pricePerShare ?? 0),
    charges: mapCharges(raw.charges as IndianMarketCharges),
    comments: raw.comments as string | undefined,
    partnerAllocations: ((raw.partnerAllocations as Record<string, unknown>[]) ?? []).map(mapAllocation),
  };
}

function mapSell(raw: Record<string, unknown>): PlanSell {
  const id = raw.id != null && String(raw.id).trim() !== '' ? String(raw.id) : undefined;
  return {
    id: id ?? '',
    tradeDate: formatApiDate(raw.tradeDate),
    quantity: Number(raw.quantity ?? 0),
    pricePerShare: Number(raw.pricePerShare ?? 0),
    capitalGainsType: (raw.capitalGainsType as 'STCG' | 'LTCG') ?? 'STCG',
    charges: mapCharges(raw.charges as IndianMarketCharges),
    comments: raw.comments as string | undefined,
  };
}

function mapAllocation(raw: Record<string, unknown>): PlanCapitalAllocation {
  return {
    id: raw.id as string | undefined,
    capitalPartnerId: String(raw.capitalPartnerId ?? ''),
    partnerName: raw.partnerName as string | undefined,
    allocatedAmount: Number(raw.allocatedAmount ?? 0),
    note: raw.note as string | undefined,
  };
}

function mapExecution(raw: Record<string, unknown>): PlanExecutionData {
  const purchases = ((raw.purchases as Record<string, unknown>[]) ?? []).map(mapPurchase);
  return {
    purchases,
    purchase: purchases[0],
    sells: ((raw.sells as Record<string, unknown>[]) ?? []).map(mapSell),
    partnerAllocations: ((raw.partnerAllocations as Record<string, unknown>[]) ?? []).map(mapAllocation),
    totalPurchaseCost: raw.totalPurchaseCost != null ? Number(raw.totalPurchaseCost) : undefined,
  };
}

export async function getPlanExecution(planId: string): Promise<PlanExecutionData> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/execution`,
  });
  return mapExecution((response.data as ApiEnvelope<Record<string, unknown>>).data);
}

export async function savePlanPurchase(
  planId: string,
  purchase: Omit<PlanPurchase, 'id'> & {
    id?: string;
    partnerAllocations?: PlanCapitalAllocation[];
  }
): Promise<PlanExecutionData> {
  await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/execution/purchase`,
    data: {
      id: purchase.id,
      tradeDate: purchase.tradeDate,
      quantity: purchase.quantity,
      pricePerShare: purchase.pricePerShare,
      charges: purchase.charges,
      comments: purchase.comments,
      partnerAllocations: (purchase.partnerAllocations ?? [])
        .filter((r) => r.capitalPartnerId && (r.allocatedAmount ?? 0) > 0)
        .map((r) => ({
          id: r.id,
          capitalPartnerId: r.capitalPartnerId,
          allocatedAmount: r.allocatedAmount,
          note: r.note,
        })),
    },
  });
  return getPlanExecution(planId);
}

export async function savePlanSell(planId: string, sell: Omit<PlanSell, 'id' | 'capitalGainsType'> & { id?: string }): Promise<PlanExecutionData> {
  await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/execution/sells`,
    data: {
      id: sell.id,
      tradeDate: sell.tradeDate,
      quantity: sell.quantity,
      pricePerShare: sell.pricePerShare,
      charges: sell.charges,
      comments: sell.comments,
    },
  });
  return getPlanExecution(planId);
}

export async function deletePlanSell(planId: string, sellId: string): Promise<PlanExecutionData> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/execution/sells/${encodeURIComponent(sellId)}`,
  });
  return getPlanExecution(planId);
}

export async function deletePlanPurchase(planId: string, purchaseId: string): Promise<PlanExecutionData> {
  const response = await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${BASE}/stock-plans/${encodeURIComponent(planId)}/execution/purchases/${encodeURIComponent(purchaseId)}`,
  });
  return mapExecution((response.data as ApiEnvelope<Record<string, unknown>>).data);
}
